import Modal from '@paytrace/pt-skel/Component/Modal'
import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import {
  AchDetails,
  ACHMerchantSettings,
  Customer,
  CustomerAchDetails,
  CustomerRecord,
  RecurFormData,
} from 'types'
import { AppCtx } from '@paytrace/pt-skel/Context'
import { CompanyRecord, MerchantRecord } from '@paytrace/pt-skel/type'
import { EnvelopeIcon } from '../../Component/Icons/EnvelopeIcon'
import Event from '../../Event'
import PubSub from '@paytrace/pt-skel/Event/PubSub'

type EmailResponse = {
  error_code: number;
  message: string | undefined;
  message_id: string;
  submitted_at: string;
  to: string;
};

function formatUSD (amount: number | string) {
  const amountNum = Number(amount)
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
  return formatter.format(amountNum)
}

interface AddressInfoProps {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

function EmailAlert ({
  sendEmail,
  sentEmailResponse,
}: {
  sendEmail: boolean;
  sentEmailResponse: EmailResponse | null;
}) {
  if (
    sendEmail &&
    sentEmailResponse &&
    sentEmailResponse.error_code > 0 &&
    sentEmailResponse.message
  ) {
    return (
      <div className="alert alert-danger">{sentEmailResponse?.message}</div>
    )
  }

  if (sentEmailResponse?.error_code === 0) {
    return (
      <div className="alert alert-success">
        The consent form has been successfully emailed to{' '}
        {sentEmailResponse?.to ?? ''}
      </div>
    )
  }

  return <></>
}

const AddressInfo: React.FC<AddressInfoProps> = ({
  address,
  address2,
  city,
  state,
  zip_code,
}) => {
  if (!address || !city || !state || !zip_code) {
    return null
  }

  return (
    <div style={{ marginBottom: '8px' }}>
      <div>{address}</div>
      {address2 && <div>{address2}</div>}
      <div>
        {city}, {state} {zip_code}
      </div>
    </div>
  )
}

interface CompanyInfoProps {
  company: CompanyRecord;
}

const CompanyInfo: React.FC<CompanyInfoProps> = ({ company }) => {
  const {
    merchant_name,
    address,
    address2,
    city,
    state,
    zip_code,
    phone,
    fax,
  } = company ?? {}

  return (
    <div style={{ marginBottom: '1rem', lineHeight: '1.2rem' }}>
      <div>
        <b>{merchant_name}</b>
      </div>
      <AddressInfo
        address={address}
        address2={address2}
        city={city}
        state={state}
        zip_code={zip_code}
      />
      <div style={{ lineHeight: '1.5rem', marginBottom: '16px' }}>
        <div>{phone ?? ''}</div>
        <div>{fax ?? ''}</div>
      </div>
    </div>
  )
}

interface CompanyFooterProps {
  company: CompanyRecord;
}

const CompanyFooter: React.FC<CompanyFooterProps> = ({ company }) => (
  <div>
    <div style={{ fontWeight: 'bold' }}>{company.consent_footer1}</div>
  </div>
)

interface PrinterControlsProps {
  onPrint: () => void;
  onHelp: string;
}

const PrinterControls: React.FC<PrinterControlsProps> = ({
  onPrint,
  onHelp,
}) => {
  return (
    <>
      <a onClick={onPrint} style={{ cursor: 'pointer' }}>
        Print
      </a>
      &nbsp;| <a href={onHelp}>Help</a>
    </>
  )
}

interface ReceiptHeadersProps {
  company: CompanyRecord;
}

const ReceiptHeaders: React.FC<ReceiptHeadersProps> = ({ company }) => {
  return (
    <div style={{ marginBottom: '16px', fontWeight: 'bold' }}>
      <div style={{ marginBottom: '4px' }}>
        {company?.receipt_header_1 ?? ''}
      </div>
      <div style={{ marginBottom: '4px' }}>
        {company?.receipt_header_2 ?? ''}
      </div>
      <div style={{ marginBottom: '4px' }}>
        {company?.receipt_header_3 ?? ''}
      </div>
      <div>{company?.receipt_header_4 ?? ''}</div>
    </div>
  )
}

interface TermsAndConditionsProps {
  merchant: MerchantRecord;
  formData: RecurFormData;
  isSurchargeEnabled: boolean;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  merchant,
  formData,
  isSurchargeEnabled,
}) => {
  let startDateToDisplay: string

  if (formData.next_date instanceof Date) {
    startDateToDisplay = formData?.next_date.toLocaleDateString()
  } else if (typeof formData.next_date === 'string') {
    startDateToDisplay = formData.next_date
  } else {
    startDateToDisplay = 'Invalid Date'
  }

  return (
    <div
      style={{
        paddingBottom: '16px',
        maxWidth: '250px',
        margin: '0 auto',
        textAlign: 'left',
        lineHeight: '1.1rem',
      }}
    >
      {`I authorize ${
        merchant.merchant_name
      } to keep my credit/debit card and/or
      checking account information and signature on file in order to charge my
      credit/debit card and/or checking account for ${formData.transaction_count}
      payments of ${formatUSD(formData?.amount ?? 0)} beginning 
      ${startDateToDisplay}.`}

      {isSurchargeEnabled &&
        ` I also authorize ${merchant.merchant_name} to
        add a surcharge fee to this payment if a credit card is used. I am aware
        that the surcharge fee can vary based on merchant effective rate changes
        but will never exceed 3 percent. I am also aware that I can avoid this
        surcharge fee by using alternative payment methods.`}
    </div>
  )
}

const CustomerSignature: React.FC = () => (
  <div
    style={{
      margin: '16px auto',
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '250px',
    }}
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        paddingBottom: '16px',
      }}
    >
      <div>___________________________________</div>
      <div>Cardholder Signature</div>
    </div>
    <div>Date: ________________</div>
  </div>
)

interface CardholderDetailsProps {
  customer: Customer & CustomerRecord;
 customerAchDetails: AchDetails | null;
}

const CardholderDetails: React.FC<CardholderDetailsProps> = ({ customer, customerAchDetails }) => {
  const getLastFourOfCard = (cardMasked: string) => {
    if (cardMasked && cardMasked.length >= 4) {
      return `************${cardMasked.slice(-4)}`
    }

    return ''
  }

  return (
    <div
      style={{
        lineHeight: '1.2',
        marginBottom: '16px',
        maxWidth: '250px',
        margin: '0 auto',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          textAlign: 'left',
          justifyContent: 'space-between',
        }}
      >
        <span>Cardholder Name:</span>
        <span>{customer?.name ?? ''}</span>
      </div>
      {customerAchDetails?.routing_number && customerAchDetails?.account_number && ( // checking acount && Routing Number
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Checking Account:</span>
            <span>{getLastFourOfCard(customerAchDetails.account_number)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Routing Number</span>
            <span>{getLastFourOfCard(customerAchDetails.routing_number)}</span>
          </div>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Card Number:</span>
        <span>{getLastFourOfCard(customer?.card_masked)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Expiration Date:</span>
        <span>{`${customer?.exp_month}/${customer?.exp_year}`}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Customer Phone:</span>
        <span>{customer.phone}</span>
      </div>
    </div>
  )
}

interface PrintableConsentFormProps {
  isOpen: boolean;
  onClose: () => void;
  formData: RecurFormData;
  achMerchantSettings: ACHMerchantSettings | undefined;
  customer: Customer & CustomerRecord;
  showSurchargeInfo: boolean;
  customerAchDetails: CustomerAchDetails;
}

function PrintableConsentForm ({
  isOpen,
  onClose,
  formData,
  achMerchantSettings,
  customer,
  showSurchargeInfo,
  customerAchDetails,
}: PrintableConsentFormProps) {
  const context = useContext(AppCtx)
  const { records } = context
  const [sentEmailResponse, setSentEmailResponse] =
    useState<EmailResponse | null>(null)
  const [sendEmail, setSendEmail] = useState(false)
  const [email, setEmail] = useState('')

  const handlePrint = () => {
    window.print()
  }

  const handleEmail = () => {
    setSendEmail(true)
    if (email) {
      PubSub.pub(Event.POST_RECURRENCE_CONSENT_FORM_REQUEST, {
        email,
        recurring_id: Number(formData.id),
        merchant_id: Number(achMerchantSettings?.merchant_id),
      })
    }
  }

  useEffect(() => {
    const handleResponse = (data: {data: EmailResponse}) => {
      setSentEmailResponse({
        error_code: data.data.error_code,
        message: data.data.message,
        message_id: data.data.message_id,
        submitted_at: data.data.submitted_at,
        to: data.data.to,
      })
    }

    PubSub.sub(
      self,
      Event.POST_RECURRENCE_CONSENT_FORM_RESPONSE,
      handleResponse
    )

    return () => {
      PubSub.unsub(Event.POST_RECURRENCE_CONSENT_FORM_RESPONSE)
    }
  }, [sentEmailResponse])

  const handleEmailOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSentEmailResponse(null)
    setEmail(event.target.value)
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => onClose()}
      size="small"
      width="767px"
      className="printable-consent-form"
    >
      <div
        style={{
          lineHeight: '1',
          justifyContent: 'center',
          textAlign: 'center',
          maxWidth: '767px',
          top: '0',
          position: 'relative',
        }}
      >
        <div style={{ fontWeight: 'bold' }}>
          <PrinterControls
            onPrint={handlePrint}
            onHelp="https://paytrace.atlassian.net/wiki/spaces/HD/overview"
          />
        </div>
        <div style={{ display: 'flex' }}>
          <input
            type="text"
            placeholder="test@paytrace.com"
            onChange={handleEmailOnChange}
            style={{
              height: '45px',
              border: '1px solid #ccc',
              borderBottomLeftRadius: '4px',
              borderTopLeftRadius: '4px',
              boxSizing: 'border-box',
              padding: '0 1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              flex: 1,
              marginTop: '.50rem',
            }}
          />
          <button
            onClick={handleEmail}
            style={{
              height: '45px',
              backgroundColor: '#f7931e',
              color: 'white',
              border: '1px solid #f7931e',
              borderRadius: '4px',
              cursor: 'pointer',
              minWidth: '110px',
              padding: '.75rem .75rem',
              display: 'flex',
              gap: '.25rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EnvelopeIcon /> Email
          </button>
        </div>
        <EmailAlert
          sendEmail={sendEmail}
          sentEmailResponse={sentEmailResponse}
        />
        <hr />
        <div style={{ marginBottom: '16px', fontWeight: 'bold' }}>
          Recurring Payment Consent Form
        </div>
        <ReceiptHeaders company={records.company} />
        <CompanyInfo company={records.company} />
        <div style={{ marginBottom: '1rem' }}>
          {formData.merchant_local_time}
        </div>
        <TermsAndConditions
          formData={formData}
          isSurchargeEnabled={showSurchargeInfo}
          merchant={records.merchant}
        />
        <CardholderDetails customer={customer} customerAchDetails={customerAchDetails} />
        <CustomerSignature />
        <CompanyFooter company={records.company} />
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button onClick={() => onClose()}>Close</button>
        </div>
      </div>
    </Modal>
  )
}

export default PrintableConsentForm
